/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Spinner, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa6';
import { MainTemplate } from '../../templates/MainTemplate';
import { Text } from '../../components/atoms/Text';
import { Button } from '../../components/atoms/Button';
import { useTheme } from '../../contexts/ThemeProvider';
import { useProfile } from '../../contexts/ProfileProvider';
import { APP_EVENTS, spawnAppEvent } from '../../service/utils';
import { masks } from '../../utils/masks';
import { BookingDraft } from '../../service/telemedicine/api';
import { useOpenAppointment } from './hooks/useOpenAppointment';
import { OpenAppointmentCard } from './components/OpenAppointmentCard';
import { TelemedicineStepper } from './components/TelemedicineStepper';
import { ConsentStep } from './components/steps/ConsentStep';
import { PatientAccessStep } from './components/steps/PatientAccessStep';
import { DocumentCaptureStep } from './components/steps/DocumentCaptureStep';
import { SchedulingStep } from './components/steps/SchedulingStep';
import { CompletionStep } from './components/steps/CompletionStep';

// WhatsApp da clínica de telemedicina — dúvidas com o médico antes ou depois do atendimento.
const CLINIC_WHATSAPP = process.env.REACT_APP_TELEMEDICINE_CLINIC_WHATSAPP || '556130554040';

const STEP_LABELS = ['Termos', 'Dados do paciente', 'Validar documento', 'Agendar atendimento', 'Concluído'];

const isTelemedicineProduct = (product: any) =>
  product?.default_product?.id === 5 || product?.default_product?.name === 'Telemedicina';

export function TelemedicineSchedulePage() {
  const { cellphone, colors, isApp, isIOS, isLoadingBenefits, isProductSuperApp, productBenefits, getValidSubscriptions } =
    useTheme();
  const { profile, subscriptions } = useProfile();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState('holder');
  // Preenchido quando o paciente clica "Continuar" no passo de agendamento — a reserva de
  // fato só acontece quando ele confirma no passo seguinte (resumo + "Confirmar agendamento").
  const [bookingDraft, setBookingDraft] = useState<BookingDraft | null>(null);
  const [isBookingConfirmed, setIsBookingConfirmed] = useState(false);

  // Consulta em aberto é verificada já na abertura da tela: enquanto existir uma, o
  // usuário não passa pelas etapas de agendamento — só acessa ou cancela a que já tem.
  const openAppointment = useOpenAppointment();

  // Cada avanço de etapa empilha uma entrada de histórico, para que o botão
  // físico/gestual de voltar do celular (que dispara popstate na webview)
  // volte uma etapa em vez de sair da página.
  const goToStep = useCallback((step: number) => {
    window.history.pushState({ telemedicineStep: step }, '');
    setCurrentStep(step);
  }, []);

  const goBackStep = useCallback(() => {
    window.history.back();
  }, []);

  useEffect(() => {
    const handlePopState = () => setCurrentStep((step) => Math.max(0, step - 1));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const telemedicineProduct = useMemo(
    () => productBenefits?.find((product: any) => isTelemedicineProduct(product)),
    [productBenefits]
  );

  const telemedicineSubscription = useMemo(() => {
    if (!telemedicineProduct) return undefined;

    const validSubscriptions = getValidSubscriptions(subscriptions);
    return validSubscriptions.find((subscription: any) => subscription?.plan?.product === telemedicineProduct.id);
  }, [getValidSubscriptions, subscriptions, telemedicineProduct]);

  const mappedPlan = useMemo(() => {
    if (!telemedicineSubscription) return undefined;

    return (
      telemedicineProduct?.plans?.find((plan: any) => plan.id === telemedicineSubscription.plan.id) ??
      telemedicineProduct?.plans?.find((plan: any) => plan.level === telemedicineSubscription.plan.level) ??
      telemedicineSubscription.plan
    );
  }, [telemedicineProduct, telemedicineSubscription]);

  const patientOptions = useMemo(() => {
    const cpf = profile?.cpf ? masks.cpf(profile.cpf) : 'CPF não informado';
    const options = [
      {
        value: 'holder',
        label: `Titular - ${profile?.fullName || 'Usuário'}`,
        name: profile?.fullName || '',
        cpf,
      },
    ];

    const extraCpfs = telemedicineSubscription?.plan?.extracpfs ?? [];
    extraCpfs.forEach((extraCpf: string, index: number) => {
      options.push({
        value: `dependent-${index}`,
        label: `Dependente ${index + 1} - ${masks.cpf(extraCpf)}`,
        name: '',
        cpf: masks.cpf(extraCpf),
      });
    });

    return options;
  }, [profile, telemedicineSubscription]);

  const selectedPatientData = patientOptions.find((option) => option.value === selectedPatient) ?? patientOptions[0];

  const handleOpenLink = useCallback(
    (url: string) => {
      if (isApp) spawnAppEvent(APP_EVENTS.OPEN_LINK, url);
      else window.open(url, '_blank', 'noopener,noreferrer');
    },
    [isApp]
  );

  const handleContactProvider = useCallback(() => {
    const phoneNumber = cellphone ? masks.whatsapp(cellphone) : '5531984011893';
    const message = 'Olá, preciso de ajuda para ativar minha assinatura de Telemedicina.';
    handleOpenLink(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`);
  }, [cellphone, handleOpenLink]);

  const handleContactClinic = useCallback(() => {
    const message = 'Olá, vim pela Partiu e gostaria de tirar uma dúvida com o médico da telemedicina.';
    handleOpenLink(`https://wa.me/${CLINIC_WHATSAPP}?text=${encodeURIComponent(message)}`);
  }, [handleOpenLink]);

  useEffect(() => {
    setCurrentStep((step) => (step > 1 ? 1 : step));
  }, [selectedPatient]);

  const shouldValidateTelemedicineProduct = isProductSuperApp || (productBenefits?.length ?? 0) > 0;
  const isLegacyTelemedicineAccess = !shouldValidateTelemedicineProduct;
  const hasTelemedicineProduct = !shouldValidateTelemedicineProduct || !!telemedicineProduct;
  const hasTelemedicineSubscription = !shouldValidateTelemedicineProduct || !!telemedicineSubscription;
  const planName = telemedicineSubscription?.plan?.name ?? 'Plano não identificado';
  const mappedPlanName = mappedPlan?.name ?? planName;

  const isCheckingAppointment = openAppointment.phase === 'checking';
  const hasOpenAppointment = openAppointment.phase === 'has-existing' && !!openAppointment.appointment;
  // O stepper só faz sentido quando o agendamento está liberado — com consulta em aberto
  // (ou enquanto ainda estamos checando) não há etapa nenhuma a percorrer.
  const showStepper = !isCheckingAppointment && !hasOpenAppointment;

  // Sem stepper não há etapa para onde voltar — o botão volta a sair da tela.
  return (
    <MainTemplate goBack={showStepper && currentStep > 0 ? goBackStep : undefined}>
      <Box
        marginTop={{ base: '8px', md: '16px' }}
        padding={{ base: '10px', md: '16px' }}
        width={'100%'}
      >
        <VStack align={'start'} spacing={0} marginBottom={'8px'}>
          <Text fontSize={{ base: '17px', md: '20px' }} fontWeight={700}>
            Telemedicina
          </Text>
          <Text fontSize={'13px'} color={'#4A5568'}>
            {hasOpenAppointment
              ? 'Atendimento médico online, sem sair de casa. Acompanhe abaixo o atendimento que você já tem agendado.'
              : 'Atendimento médico online, sem sair de casa. Siga as etapas abaixo — cada uma é liberada automaticamente assim que você concluir a anterior.'}
          </Text>
        </VStack>

        {showStepper && <TelemedicineStepper steps={STEP_LABELS} currentStep={currentStep} />}
      </Box>

      <Box
        padding={{ base: '10px', md: '16px' }}
        paddingTop={0}
        paddingBottom={{ base: isIOS ? '110px' : '90px', lg: '16px' }}
        width={'100%'}
      >
        <VStack spacing={{ base: 3, md: 4 }} align={'flex-start'} width={'100%'} maxWidth={'720px'} margin={'0 auto'}>
          <Box width={'100%'} padding={{ base: '12px', md: '16px' }} borderRadius={'16px'} backgroundColor={'#FFFFFF'} border={'1px solid #E2E8F0'}>
            {isCheckingAppointment && (
              <VStack spacing={3} paddingY={6}>
                <Spinner color={colors.primaryColor} size={'lg'} />
                <Text fontSize={'14px'} color={'#4A5568'}>
                  Verificando se já existe um agendamento em aberto...
                </Text>
              </VStack>
            )}

            {hasOpenAppointment && openAppointment.appointment && (
              <OpenAppointmentCard
                appointment={openAppointment.appointment}
                isCancelling={openAppointment.isCancelling}
                cancelError={openAppointment.cancelError}
                onCancel={openAppointment.cancel}
              />
            )}

            {showStepper && currentStep === 0 && <ConsentStep onAccepted={() => goToStep(1)} />}

            {showStepper && currentStep === 1 && (
              <PatientAccessStep
                isLoadingBenefits={isLoadingBenefits}
                hasTelemedicineProduct={hasTelemedicineProduct}
                hasTelemedicineSubscription={hasTelemedicineSubscription}
                isLegacyTelemedicineAccess={isLegacyTelemedicineAccess}
                planName={planName}
                patientOptions={patientOptions}
                selectedPatient={selectedPatient}
                onSelectPatient={setSelectedPatient}
                profile={profile}
                onContactProvider={handleContactProvider}
                onConfirmed={() => goToStep(2)}
              />
            )}

            {showStepper && currentStep === 2 && (
              <DocumentCaptureStep
                cpf={masks.unmask(selectedPatientData?.cpf ?? '')}
                patientKey={selectedPatient}
                onValidated={() => goToStep(3)}
              />
            )}

            {showStepper && currentStep === 3 && (
              <SchedulingStep
                patientName={selectedPatientData?.name || undefined}
                patientEmail={profile?.email}
                patientCpf={selectedPatientData?.cpf}
                patientPhone={profile?.telephone ? masks.cell(profile.telephone) : ''}
                planName={planName}
                mappedPlanName={mappedPlanName}
                subscriptionId={telemedicineSubscription?.id ? String(telemedicineSubscription.id) : ''}
                onContinue={(draft) => {
                  setBookingDraft(draft);
                  goToStep(4);
                }}
              />
            )}

            {showStepper && currentStep === 4 && bookingDraft && (
              <CompletionStep
                draft={bookingDraft}
                planName={mappedPlanName}
                onOpenAppointmentDetected={openAppointment.refresh}
                onConfirmed={() => setIsBookingConfirmed(true)}
              />
            )}
          </Box>

          {/* Só depois de agendar: com consulta em aberto ou logo após confirmar. */}
          {(hasOpenAppointment || isBookingConfirmed) && (
            <Box width={'100%'} padding={{ base: '12px', md: '16px' }} borderRadius={'16px'} backgroundColor={'#FFFFFF'} border={'1px solid #E2E8F0'}>
              <Text fontSize={'14px'} fontWeight={700}>
                Dúvidas com o médico?
              </Text>
              <Text fontSize={'13px'} color={'#4A5568'} marginBottom={3}>
                Antes ou depois do atendimento, fale com a clínica pelo WhatsApp.
              </Text>
              <Button leftIcon={<FaWhatsapp />} onClick={handleContactClinic}>
                Falar com a clínica
              </Button>
            </Box>
          )}
        </VStack>
      </Box>
    </MainTemplate>
  );
}
